import React, { useRef, useState } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Monitor,
  ClipboardList,
  MonitorSmartphone,
  ArrowRight,
  Sparkles,
  Zap,
  Eye,
  TrendingUp,
  CheckCircle,
  Cpu,
  Network,
  Server,
  Database,
  Settings,
} from 'lucide-react';

/**
 * Lightweight image component with fallback handling.
 * Kept inline so you can drop this into any project without extra imports.
 */
const ImageWithFallback: React.FC<{
  src: string;
  alt: string;
  className?: string;
  fallbackSrc?: string;
}> = ({ src, alt, className, fallbackSrc }) => {
  const [imgSrc, setImgSrc] = useState(src);
  return (
    <img
      loading="lazy"
      src={imgSrc}
      alt={alt}
      className={className}
      onError={() => {
        if (fallbackSrc && imgSrc !== fallbackSrc) setImgSrc(fallbackSrc);
      }}
    />
  );
};

interface ITProduct {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  icon: React.ComponentType<any>;
  logo?: string;
  features: string[];
  benefits: string[];
  path: string;
  gradient: string; // Tailwind gradient classes
  imageUrl: string; // ✅ explicit per-product image link
  imageAlt?: string;
}

// ✅ Separate, explicit image URLs for each product (Unsplash CDN). Replace with your assets anytime.
const itProducts: ITProduct[] = [
  {
    id: 'manageRTC',
    title: 'ManageRTC',
    subtitle: 'Integrated HR and Project Management Platform',
    description:
      'ManageRTC is a unified digital platform that automates human resource and project management workflows. It helps organizations manage recruitment, payroll, attendance, performance, task allocation, and project tracking in one integrated system, improving efficiency and transparency across teams.',
    icon: ClipboardList,
    logo: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=100&h=100&fit=crop',
    features: [
      'Centralized employee and project data management',
      'Automated payroll, attendance, and leave tracking',
      'Performance evaluation and goal setting tools',
      'Task assignment, progress tracking, and project milestones',
      'Self service portals for employees, managers, and project leads',
      'Compliance with labor laws, tax rules, and project standards',
      'Analytics dashboards for workforce and project insights'
    ],
    benefits: [
      'Improves productivity through unified HR and project operations',
      'Reduces manual effort with automation and self service features',
      'Enhances accuracy in payroll, attendance, and project reporting',
      'Promotes transparency and accountability across teams',
      'Provides real time insights for better decision making'
    ],
    path: '/products/it/ManageRTC',
    gradient: 'from-blue-500 to-indigo-600',
    imageUrl: '/assets/Products/it-Products/manageRTC.png',
    imageAlt: 'Integrated HR and project management dashboard showing tasks, payroll, and performance metrics'
  },

  {
  id: 'websites',
  title: 'Websites',
  subtitle: 'Digital Excellence Delivered',
  description:
    'We design and develop visually engaging, high performance, and secure websites that deliver measurable business results. Each site is fully responsive, SEO optimized, and built with modern technology stacks, including CMS, payment gateways, and analytics integration for complete digital scalability.',
  icon: MonitorSmartphone,
    logo: '',
  features: [
    'Responsive and mobile first web design',
    'SEO optimization and advanced security practices',
    'Fast loading and performance tuned architecture',
    'Integration with CMS, payment gateways, and APIs',
    'Analytics and conversion tracking dashboards',
    'Custom design systems for brand consistency'
  ],
  benefits: [
    'Enhances brand presence and credibility',
    'Drives measurable growth and conversions',
    'Delivers an optimized user experience on all devices',
    'Improves search engine visibility and ranking',
    'Supports long term scalability and easy maintenance'
  ],
  path: '/products/it/Websites',
  gradient: 'from-purple-500 to-violet-600',
  imageUrl: '/assets/Products/it-Products/websites.png',
  imageAlt: 'Responsive website design displayed on laptop, tablet, and smartphone'
}

];

const FloatingIcon: React.FC<{ icon: React.ComponentType<any>; delay: number }> = ({ icon: Icon, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: [0, 1, 0.7, 1], y: [20, 0, -10, 0], rotate: [0, 5, -5, 0] }}
    transition={{ duration: 4, delay, repeat: Infinity, repeatType: 'reverse' }}
    className="absolute"
  >
    <Icon className="h-5 w-5 text-primary/40 md:h-6 md:w-6" />
  </motion.div>
);

const ITParticleField: React.FC = () => {
  const icons = [Monitor, Server, Database, Network, Settings, Cpu];
  const positions = [
    { top: '10%', left: '10%' },
    { top: '20%', right: '15%' },
    { top: '40%', left: '5%' },
    { top: '60%', right: '10%' },
    { top: '80%', left: '20%' },
    { top: '15%', left: '50%' },
    { top: '35%', right: '30%' },
    { top: '70%', left: '60%' },
    { top: '25%', right: '50%' },
    { top: '85%', right: '40%' },
    { top: '45%', left: '80%' },
    { top: '65%', left: '30%' },
    { top: '30%', right: '20%' },
    { top: '75%', right: '60%' },
    { top: '55%', left: '40%' },
  ];

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {Array.from({ length: 15 }).map((_, i) => {
        const Icon = icons[i % icons.length];
        const position = positions[i];
        return (
          <div key={i} className="absolute" style={position}>
            <FloatingIcon icon={Icon} delay={i * 0.3} />
          </div>
        );
      })}
    </div>
  );
};

const ProductCard: React.FC<{ product: ITProduct; index: number }> = ({ product, index }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });
  const Icon = product.icon;

  return (
    <section id={product.id} ref={ref} className="relative scroll-mt-24 py-16 md:py-15 lg:py-10">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 80 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 80 }}
          transition={{ duration: 0.7, delay: index * 0.05 }}
          className={`grid items-center gap-10 md:gap-14 lg:gap-16 lg:grid-cols-2 ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}
        >
          {/* Content Side */}
          <motion.div
            initial={{ opacity: 0, x: index % 2 === 0 ? -80 : 80 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: index % 2 === 0 ? -80 : 80 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className={`${index % 2 === 1 ? 'lg:col-start-2' : ''}`}
          >
            {/* Header */}
            <div className="mb-6 flex items-center gap-4 sm:mb-8">
              <motion.div
                whileHover={{ rotate: product.logo ? 0 : 360, scale: 1.1 }}
                transition={{ duration: 0.6 }}
                className={`neon-glow rounded-xl p-3 sm:p-4 ${product.logo ? 'bg-white/10' : `bg-gradient-to-br ${product.gradient}`}`}
                aria-hidden
              >
                {product.logo ? (
                  <img 
                    src={product.logo} 
                    alt={`${product.title} logo`}
                    className="h-10 w-10 sm:h-12 sm:w-12 object-contain"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const parent = e.currentTarget.parentElement;
                      if (parent) {
                        parent.className = `neon-glow rounded-xl p-3 sm:p-4 bg-gradient-to-br ${product.gradient}`;
                      }
                    }}
                  />
                ) : (
                  <Icon className="h-6 w-6 text-white sm:h-7 sm:w-7" />
                )}
              </motion.div>
              <div>
                <motion.h2
                  initial={{ opacity: 0, y: 16 }}
                  animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
                  transition={{ duration: 0.5, delay: 0.25 }}
                  className="mb-1 text-2xl font-bold text-white sm:text-3xl lg:text-4xl"
                >
                  {product.title}
                </motion.h2>
                <motion.p
                  initial={{ opacity: 0, y: 16 }}
                  animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className={`text-md font-semibold text-transparent bg-clip-text bg-gradient-to-r ${product.gradient}`}
                >
                  {product.subtitle}
                </motion.p>
              </div>
            </div>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
              transition={{ duration: 0.5, delay: 0.35 }}
              className="mb-8 max-w-prose text-base leading-relaxed text-muted-foreground sm:text-lg text-justify"
            >
              {product.description}
            </motion.p>

            {/* Features & Benefits */}
            <div className="mb-8 grid gap-6 md:grid-cols-2 md:gap-8">
              {/* Features */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="glass rounded-xl p-5 sm:p-6"
              >
                <div className="mb-3 flex items-center gap-2 sm:mb-4">
                  <Zap className="h-5 w-5 text-primary" />
                  <h4 className="font-semibold text-white">Key Features</h4>
                </div>
                <ul className="space-y-2">
                  {product.features.map((feature, idx) => (
                    <motion.li
                      key={idx}
                      initial={{ opacity: 0, x: -16 }}
                      animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -16 }}
                      transition={{ duration: 0.35, delay: 0.45 + idx * 0.08 }}
                      className="flex items-start gap-2 text-sm text-muted-foreground"
                    >
                      <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent" />
                      <span>{feature}</span>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>

              {/* Benefits */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.5, delay: 0.45 }}
                className="glass rounded-xl p-5 sm:p-6"
              >
                <div className="mb-3 flex items-center gap-2 sm:mb-4">
                  <TrendingUp className="h-5 w-5 text-secondary" />
                  <h4 className="font-semibold text-white">Benefits</h4>
                </div>
                <ul className="space-y-2">
                  {product.benefits.map((benefit, idx) => (
                    <motion.li
                      key={idx}
                      initial={{ opacity: 0, x: -16 }}
                      animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -16 }}
                      transition={{ duration: 0.35, delay: 0.5 + idx * 0.08 }}
                      className="flex items-start gap-2 text-sm text-muted-foreground"
                    >
                      <Sparkles className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent" />
                      <span>{benefit}</span>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            </div>

            {/* CTA Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: 0.55 }}
            >
              <Link
                to={product.path}
                className="group inline-flex items-center gap-3 rounded-xl bg-gradient-to-r from-primary to-secondary px-6 py-3 font-semibold text-white shadow transition-all duration-300 hover:shadow-2xl hover:shadow-primary/25 sm:px-8 sm:py-4"
              >
                Learn More
                <motion.span whileHover={{ x: 4 }} transition={{ duration: 0.2 }}>
                  <ArrowRight className="h-5 w-5" />
                </motion.span>
              </Link>
            </motion.div>
          </motion.div>

          {/* Image Side */}
          <motion.div
            initial={{ opacity: 0, x: index % 2 === 0 ? 80 : -80, rotateY: index % 2 === 0 ? -10 : 10 }}
            animate={isInView ? { opacity: 1, x: 0, rotateY: 0 } : { opacity: 0, x: index % 2 === 0 ? 80 : -80, rotateY: index % 2 === 0 ? -10 : 10 }}
            transition={{ duration: 0.8, delay: 0.25 }}
            className={`relative perspective-1000 ${index % 2 === 1 ? 'lg:col-start-1' : ''}`}
          >
            <div className="relative group">
              {/* Glow */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                className={`absolute -inset-6 rounded-full blur-3xl opacity-20 bg-gradient-to-r ${product.gradient}`}
                aria-hidden
              />

              {/* Main Image */}
              <motion.div
                whileHover={{ scale: 1.03, rotateY: 4, rotateX: 2 }}
                transition={{ duration: 0.35 }}
                className="relative z-10 overflow-hidden rounded-2xl border border-white/5 bg-white/5 shadow-xl backdrop-blur-sm"
              >
                <div className="aspect-square w-full">
                  <ImageWithFallback
                    src={product.imageUrl}
                    fallbackSrc="https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=1600&auto=format&fit=crop"
                    alt={product.imageAlt || product.title}
                    className="h-full w-full object-cover"
                  />
                </div>

                {/* Overlay with IT Elements */}
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent">
                  <div className="absolute bottom-4 left-4 right-4 sm:bottom-6 sm:left-6 sm:right-6">
                    <motion.div
                      initial={{ opacity: 0, y: 12 }}
                      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }}
                      transition={{ duration: 0.45, delay: 0.6 }}
                      className="flex items-center gap-3 text-white"
                    >
                      <div className="rounded-lg bg-primary/20 p-2 backdrop-blur-sm">
                        <Monitor className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold">IT-Powered</p>
                        <p className="text-xs text-white/80 sm:text-sm">Digital Excellence</p>
                      </div>
                    </motion.div>
                  </div>
                </div>
              </motion.div>

              {/* Floating UI Badge */}

            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export const ITProductsPage: React.FC = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { scrollYProgress } = useScroll({ target: containerRef });
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '60%']);

  // Note: Scroll-to-section behavior is now handled globally by ScrollToTop component in App.tsx

  return (
    <div ref={containerRef} className="relative min-h-screen pt-20">
      {/* Animated Background */}
      <motion.div style={{ y: backgroundY }} className="fixed inset-0 opacity-30">
        <ITParticleField />
      </motion.div>

      {/* Hero Section */}
      <section className="relative flex min-h-[70vh] items-center justify-center sm:min-h-[80vh] lg:min-h-screen">
        <div className="mx-auto max-w-5xl px-4 text-center sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }} className="relative">
            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="mb-4 text-4xl font-bold leading-tight sm:text-5xl lg:text-7xl"
            >
              <span className="bg-gradient-to-r from-primary via-accent to-secondary bg-clip-text text-transparent">
                IT PRODUCTS
              </span>
            </motion.h1>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="mb-6 flex items-center justify-center gap-3 sm:mb-8 sm:gap-4"
            >
              <div className="h-px w-16 bg-gradient-to-r from-transparent via-primary to-transparent sm:w-24" />
              <Monitor className="h-6 w-6 animate-pulse-glow text-primary sm:h-8 sm:w-8" />
              <div className="h-px w-16 bg-gradient-to-r from-transparent via-primary to-transparent sm:w-24" />
            </motion.div>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.45 }}
              className="mx-auto mb-4 max-w-2xl text-base text-muted-foreground text-center sm:text-lg lg:text-2xl"
            >
              Empowering Businesses with Digital Precision
            </motion.p>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.55 }}
              className="mx-auto mb-10 max-w-3xl text-sm text-muted-foreground text-center sm:text-base lg:text-lg"
            >
              At amasQIS.ai, our IT solutions modernize core business processes, making them smarter, faster, and more reliable. From managing your workforce to tracking multi-million-dollar projects, we provide platforms that keep operations smooth and transparent.
            </motion.p>

            {/* Quick Navigation */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.7 }}
              className="mb-14 flex flex-wrap justify-center gap-3 sm:gap-4"
            >
              {itProducts.map((product, index) => {
                const Icon = product.icon;
                return (
                  <motion.a
                    key={product.id}
                    href={`#${product.id}`}
                    whileHover={{ scale: 1.04, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.85 + index * 0.06 }}
                    className="glass group rounded-xl px-3 py-2 text-xs hover:glass-strong sm:px-4 sm:py-3 sm:text-sm"
                  >
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4 text-primary transition-colors group-hover:text-accent" />
                      <span className="font-medium">{product.title}</span>
                    </div>
                  </motion.a>
                );
              })}
            </motion.div>

            {/* Scroll Indicator */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 1 }} className="flex flex-col items-center gap-2">
              <p className="text-xs text-muted-foreground sm:text-sm">Explore Our IT Solutions</p>
              <motion.div
                animate={{ y: [0, 8, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="flex h-10 w-6 justify-center rounded-full border-2 border-primary/50"
                aria-hidden
              >
                <motion.div
                  animate={{ y: [0, 12, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="mt-2 h-3 w-1 rounded-full bg-primary"
                />
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Product Sections */}
      {itProducts.map((product, index) => (
        <ProductCard key={product.id} product={product} index={index} />
      ))}

      {/* Bottom CTA Section */}
      <section className="relative pb-16 pt-6 sm:pb-20 sm:pt-10 lg:pb-24 lg:pt-12">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
            <h2 className="mb-3 text-2xl font-bold sm:mb-4 sm:text-3xl lg:text-4xl">Ready to Modernize Your Business Operations?</h2>
            <p className="mx-auto mb-6 max-w-2xl text-sm text-muted-foreground sm:mb-8 sm:text-base">
              Experience the power of digital precision and streamlined processes.
            </p>
            <div className="flex flex-col justify-center gap-3 sm:flex-row sm:gap-4">
              <Link
                to="/contact"
                className="group inline-flex items-center justify-center gap-3 rounded-xl bg-gradient-to-r from-primary to-secondary px-6 py-3 font-semibold text-white shadow transition-all duration-300 hover:shadow-2xl hover:shadow-primary/25 sm:px-8 sm:py-4"
              >
                Get Started Today
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact"
                className="glass inline-flex items-center justify-center gap-3 rounded-xl px-6 py-3 font-semibold text-white hover:glass-strong sm:px-8 sm:py-4"
              >
                Schedule Demo
                <Eye className="h-5 w-5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ITProductsPage;
